import { InventoryTypeKey } from '@carvana/merch-contracts/InventoryType';
import {
  defaultLoadableState,
  type LoadableState,
  useLoadableState
} from '@carvana/merch-react-hooks';
import deepmerge from 'deepmerge';
import React, {
  createContext,
  PropsWithChildren,
  useCallback,
  useContext,
  useMemo,
  useState
} from 'react';
import AvailabilityAction from '~/constants/availabilityAction';
import AvailabilityError from '~/constants/availabilityError';
import OrderState from '~/constants/common/OrderState';
import { VehicleLockTypeKey } from '~/constants/vehicleLockType';
import type VehiclePaymentTerm from '~/contracts/common/VehiclePaymentTerm';
import type { Availability } from '~/contracts/vdp';
import type { VehiclePricingResponse } from '~/services/common/contracts/PricingResponse';
import { PricingError } from '~/services/common/contracts/PricingResponse';
import UserPurchaseData from '~/services/common/contracts/UserPurchaseData';
import type { DeepPartial } from '~/types';
import EventManager, { useEventTrigger } from '~/utilities/EventManager';

export type ContextType = {
  fetchVehicleAvailabilityFlag: number;
  fetchVehiclePricingFlag: number;
  fetchedVehicleAvailabilityOnServer: boolean;
  isFavorite: boolean;
  isSubscribedToNotifications: boolean;
  rcbPurchasing: LoadableState<UserPurchaseData>;
  setFavorite: (isFavorite: boolean, isSubscribed?: boolean) => void;
  setPricingError: React.Dispatch<React.SetStateAction<PricingError>>;
  setSubscribedToNotifications: React.Dispatch<React.SetStateAction<boolean>>;
  subscribeToNotificationsState: LoadableState<boolean>;
  triggerEvent: EventManager;
  triggerFetchVehicleAvailability: () => void;
  triggerFetchVehiclePricing: () => void;
  vehicleAvailability: LoadableState<Availability>;
  vehicleAvailabilityFromServer: Availability | undefined;
  vehiclePricingError: PricingError;
  vehiclePricingInfo: LoadableState<Partial<VehiclePricingResponse>>;
};

export const defaultAvailability: Availability = {
  action: AvailabilityAction.None,
  error: AvailabilityError.None,
  inventoryType: InventoryTypeKey.Core,
  isAvailableForPurchase: false,
  isBuyingAVehicle: false,
  isLockedByThisUser: false,
  isPendingDealerPurchase: false,
  isPendingDealerPurchaseByUser: false,
  lockType: VehicleLockTypeKey.None
};

const defaultContext: ContextType = {
  fetchVehicleAvailabilityFlag: 0,
  fetchVehiclePricingFlag: 0,
  fetchedVehicleAvailabilityOnServer: false,
  isFavorite: false,
  isSubscribedToNotifications: false,
  rcbPurchasing: defaultLoadableState<UserPurchaseData>({
    orderState: OrderState.None
  }),
  setFavorite() {},
  setPricingError() {},
  setSubscribedToNotifications() {},
  subscribeToNotificationsState: defaultLoadableState(false),
  triggerEvent: new EventManager(false),
  triggerFetchVehicleAvailability() {},
  triggerFetchVehiclePricing() {},
  vehicleAvailability: defaultLoadableState<Availability>(defaultAvailability),
  vehicleAvailabilityFromServer: undefined,
  vehiclePricingError: PricingError.None,
  vehiclePricingInfo: defaultLoadableState<Partial<VehiclePricingResponse>>({})
};

export const UserVehicleContext = createContext<ContextType>(defaultContext);

export type OwnProps = PropsWithChildren<{
  fetchedVehicleAvailabilityOnServer?: boolean;
  vehicleAvailabilityFromServer?: Availability;
}>;

export const UserVehicleContextProvider = (props: OwnProps) => {
  const [fetchVehicleAvailabilityFlag, setFetchVehicleAvailabilityFlag] =
    useState(defaultContext.fetchVehicleAvailabilityFlag);
  const [fetchVehiclePricingFlag, setFetchVehiclePricingFlag] = useState(
    defaultContext.fetchVehiclePricingFlag
  );
  const [isFavorite, setFavorite] = useState(defaultContext.isFavorite);
  const [isSubscribedToNotifications, setSubscribedToNotifications] = useState(
    defaultContext.isSubscribedToNotifications
  );
  const rcbPurchasing = useLoadableState<UserPurchaseData>(
    defaultContext.rcbPurchasing.data
  );
  const vehiclePricingInfo = useLoadableState(
    defaultContext.vehiclePricingInfo.data
  );
  const [vehiclePricingError, setPricingError] = useState(
    defaultContext.vehiclePricingError
  );
  const subscribeToNotificationsState = useLoadableState(false);
  const vehicleAvailability = useLoadableState<Availability>(
    props.vehicleAvailabilityFromServer ??
      defaultContext.vehicleAvailability.data,
    {
      initializeAsLoaded: props.fetchedVehicleAvailabilityOnServer ?? false
    }
  );

  const eventManager = useMemo(() => new EventManager(true), []);
  const availabilityUpdateFlag = useEventTrigger(eventManager, 'availability');

  const setFavoriteAndSubscribe = useCallback<ContextType['setFavorite']>(
    (f: boolean, s?: boolean) => {
      setFavorite(f);
      if (s !== undefined) {
        setSubscribedToNotifications(s);
      }
    },
    []
  );

  const triggerFetchVehicleAvailability = useCallback(() => {
    setFetchVehicleAvailabilityFlag(Date.now());
  }, []);

  const triggerFetchVehiclePricing = useCallback(() => {
    setFetchVehiclePricingFlag(Date.now());
  }, []);

  const value = useMemo<ContextType>(
    () => ({
      fetchVehicleAvailabilityFlag: Math.abs(
        fetchVehicleAvailabilityFlag - availabilityUpdateFlag
      ),
      fetchVehiclePricingFlag,
      fetchedVehicleAvailabilityOnServer:
        props.fetchedVehicleAvailabilityOnServer ??
        defaultContext.fetchedVehicleAvailabilityOnServer,
      isFavorite,
      isSubscribedToNotifications,
      rcbPurchasing,
      setFavorite: setFavoriteAndSubscribe,
      setPricingError,
      setSubscribedToNotifications,
      subscribeToNotificationsState,
      triggerEvent: eventManager,
      triggerFetchVehicleAvailability,
      triggerFetchVehiclePricing,
      vehicleAvailability,
      vehicleAvailabilityFromServer:
        props.vehicleAvailabilityFromServer ??
        defaultContext.vehicleAvailabilityFromServer,
      vehiclePricingError,
      vehiclePricingInfo
    }),
    [
      availabilityUpdateFlag,
      eventManager,
      fetchVehicleAvailabilityFlag,
      fetchVehiclePricingFlag,
      props.fetchedVehicleAvailabilityOnServer,
      isFavorite,
      isSubscribedToNotifications,
      rcbPurchasing,
      setFavoriteAndSubscribe,
      subscribeToNotificationsState,
      triggerFetchVehicleAvailability,
      triggerFetchVehiclePricing,
      vehicleAvailability,
      props.vehicleAvailabilityFromServer,
      vehiclePricingError,
      vehiclePricingInfo
    ]
  );

  return (
    <UserVehicleContext.Provider value={value}>
      {props.children}
    </UserVehicleContext.Provider>
  );
};

UserVehicleContextProvider.displayName = 'UserVehicleContextProvider';

export default UserVehicleContext;

export const generateUserVehicleContext = (
  customState: DeepPartial<ContextType>
): ContextType =>
  deepmerge<ContextType, DeepPartial<ContextType>>(defaultContext, customState);

export function MockedUserVehicleContextProvider({
  children,
  mockValue
}: React.PropsWithChildren<{ mockValue: DeepPartial<ContextType> }>) {
  return (
    <UserVehicleContext.Provider value={generateUserVehicleContext(mockValue)}>
      {children}
    </UserVehicleContext.Provider>
  );
}

export function useVehiclePricing() {
  return useContext(UserVehicleContext).vehiclePricingInfo.data;
}

export function useVehiclePricingTradeInfo() {
  return (
    useContext(UserVehicleContext).vehiclePricingInfo.data?.tradeInfo ?? {}
  );
}

export function useVehiclePaymentTerms(): Partial<VehiclePaymentTerm> {
  const vehiclePricingInfo = useVehiclePricing();

  return vehiclePricingInfo.price ?? {};
}

export function useUserPurchasing() {
  return useContext(UserVehicleContext).rcbPurchasing.data;
}

export function useHasActiveCart() {
  const orderState = useUserPurchasing().orderState;

  return [
    OrderState.InProgress,
    OrderState.InProgressWithTradeIn,
    OrderState.OrderPlaced,
    OrderState.Reserved
  ].includes(orderState);
}

export function useVehicleAvailability() {
  return useContext(UserVehicleContext).vehicleAvailability.data;
}
